<template>
  <form
      :key="$route.fullPath"
      @submit.prevent="submitCertificate"
      autocomplete="off"
      class="w-full max-h-screen bg-background flex flex-col relative"
      :class="{ 'overflow-y-hidden': isBusy }"

  >
    <div
        v-show="isBusy"
        class="absolute inset-0 bg-faint-white flex items-center justify-center z-10"
    >
      <spinner :color="'text-black'" :size="10"/>
    </div>
    <page-header :title="pageTitle" :info="getCertificateNo" backTo="certificate"
                 :secondaryInfo="certificateStatusLabel" :secondaryInfoColor="certificateStatusClass"
                 secondaryInfoFontSize="text-sm" :lastSavedTime="certificateLastUpdated">

      <AppButton color="indigo" label="Print Certificate" @click="printCertificate" :icon=true class="mr-2">
        <template v-slot:icon>
          <Icon iconType="print" class="mr-2"/>
        </template>
      </AppButton>

      <button
          @click="saveCertificate"
          type="button"
          class="flex items-center bg-blue-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path
              d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
          />
          <path d="M17 21v-8H7v8M7 3v5h8"/>
        </svg>
        <span>Save Certificate</span>
      </button>

      <AppButton v-if="certificate.status === 'New'"
          color="green"
          label="Save & Complete"
          @click="markCertificateComplete"
          :icon="true"
          class="ml-2"
      >
        <template v-slot:icon>
          <Icon iconType="check" class="mr-2"/>
        </template>
      </AppButton>

    </page-header>


    <main class="pr-6 pl-6  bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative">
        <div class="mt-6">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Equipment Details
            </h3>
            <hr class="flex-1 border-primary ml-4"/>
            <AppButton color="green" label="Find & Select Equipment" :icon=true class="ml-2"
                       @click="clickFindEquipmentPanel()">
              <template v-slot:icon>
                <Icon iconType="search" class="mr-2"/>
              </template>
            </AppButton>
          </div>

          <div class="flex flex-col bg-white md:flex-row items-center justify-between ">


            <!-- Main Column 1 -->
            <div class=" w-full sm:w-full md:w-4/9 bg-white mx-4 rounded md:rounded-none">
              <div class="w-full flex">
                <!-- Sub Column 1 of 1 -->
                <div class="w-4/12 p-1 px-2">
                  <!-- Row 1 -->
                  <div class="">
                    <label class="uppercase font-semibold text-sm">Serial NUMBER</label>

                    <EquipmentWithSerialNumberAutoComplete class="w-full" @equipmentSelected="handleEquipmentSelected"
                                                           @equipmentCleared="handleEquipmentCleared"
                                                           @equipmentUnselected="handleEquipmentUnselected"
                                                           v-model="certificate.equipment"
                    />

                  </div>
                  <!-- Row 2 -->
                  <div class="mt-4"> <!-- Added margin-top for alignment -->
                    <label class="uppercase font-semibold text-sm">Equipment Model</label>
                    <input class="w-full bge-input bge-input-spacing rounded"
                           v-model="certificate.equipment_model"
                           type="text"/>
                  </div>

                </div>

                <!-- Sub Column 1 of 2 -->
                <div class="w-8/12 p-1 px-2">
                  <!-- Row 1 - Split into two parts -->
                  <div class="flex">
                    <div class="w-5/12 px-2"> <!-- Adjusted to 80% -->
                      <label class="uppercase font-semibold text-sm">Make</label>
                      <input class="w-full bge-input bge-input-spacing rounded" v-model="certificate.equipment_make"
                             type="text"/>
                    </div>
                    <div class="w-4/12 px-2"> <!-- Adjusted to 20% -->
                      <label class="uppercase font-semibold text-sm sw-label">SW Version</label>
                      <input class="w-full bge-input bge-input-spacing rounded" v-model="certificate.sw_version"
                             type="text"/>
                    </div>

                    <div class="w-3/12 px-2 flex flex-col justify-center items-center"> <!-- Adjusted to 20% -->
                      <label class="uppercase font-semibold text-sm">Connected</label>
                      <Toggle class="w-full override-toggle  bge-input-spacing rounded pl-4"
                              labelPosition="left"
                              label=""
                              :checked="certificate.is_connected"
                              labelClasses="uppercase font-semibold text-sm"
                              @value-changed="(value) => certificate.is_connected = value"
                      />
                    </div>
                  </div>
                  <!-- Row 2 -->
                  <div class="mt-4 mx-2"> <!-- Added margin-top for alignment -->
                    <label class="uppercase font-semibold text-sm">Description</label>
                    <input class="w-full bge-input bge-input-spacing rounded"
                           v-model="certificate.equipment_description"
                           type="text"/>
                  </div>
                </div>
              </div>
            </div>

            <div class=" md:block">
              <div class="w-px bg-orange-500 md:h-32"></div>
            </div>

            <!-- Main Column 2 -->
            <div class=" w-full md:w-3/9 sm:w-full bg-white px-2 mx-4  rounded  md:rounded-none">
              <div class="flex">
                <!-- Sub Column 2 of 1 -->
                <div class="w-8/12 px-2">
                  <!-- Row 1 -->
                  <div class="mb-2 p-1">
                    <label class="uppercase font-semibold text-sm">Engineer</label>
                    <StaffAutocomplete class="w-full" v-model="certificate.engineer" :forceSelection="true"/>
                  </div>
                  <!-- Row 2 -->
                  <div class="p-1">
                    <label class="uppercase font-semibold text-sm">Customer</label>
                    <ContactAutocomplete class="w-full" v-model="certificate.customer" :disabled=true
                                         :forceSelection="true" :is_customer="true"/>
                  </div>
                </div>
                <!-- Sub Column 2 of 2 -->
                <div class="w-4/12 px-2">
                  <!-- Row 1 -->
                  <div class="mb-2 p-1">
                    <div class="w-full">
                      <!-- Row 1 split into three parts -->
                      <label class="uppercase font-semibold text-sm">Eng No.</label>
                      <input class="w-full bge-input bge-input-spacing rounded" v-model="certificate.engineer_number"
                             type="text"/>
                    </div>
                  </div>
                  <!-- Row 2 -->
                  <div class="p-1">
                    <label class="uppercase font-semibold text-sm">Site Number</label>
                    <input class="w-full bge-input bge-input-spacing rounded" v-model="certificate.customer_vts_number"
                           type="text"/>
                  </div>

                </div>
              </div>
            </div>

            <div class=" md:block">
              <div class="w-px bg-orange-500 md:h-32"></div>
            </div>

            <!-- Main Column 3 -->
            <div class="w-full md:w-2/9 bg-white mx-4  rounded md:rounded-none">
              <div class="flex flex-wrap w-full">
                <!-- Row 1: Internal and Issue Date -->
                <div class="w-full flex items-start">
                  <!-- Internal Section -->
                  <div class="w-3/10 p-1 flex flex-col justify-center">
                    <div class="flex flex-col justify-center items-center">
                      <label class="block uppercase font-semibold text-sm mb-2">Internal</label>
                      <Toggle class="rounded override-toggle pl-2"
                              labelPosition="left"
                              label=""
                              :checked="certificate.is_internal"
                              labelClasses="uppercase font-semibold text-sm"
                              @value-changed="(value) => {
              certificate.is_internal = value;
              if (value) certificate.is_external = !value;
            }"
                      />
                    </div>
                  </div>


                  <!-- Issue Date Section -->
                  <div class="w-7/10 p-1 flex flex-col">
                    <label class="block uppercase font-semibold text-sm">Issue Date</label>
                    <Calendar appendTo="body" class="w-full"
                              dateFormat="dd/mm/yy"
                              v-model="certificate.issue_date" :showIcon="true"
                    />
                  </div>
                </div>

                <!-- Row 2: External and Expiry Date -->
                <div class="w-full flex items-start mt-3">
                  <!-- External Section -->
                  <div class="w-3/10 p-1 flex flex-col justify-center items-center">
                    <label class="block uppercase font-semibold text-sm">External</label>
                    <Toggle class="mt-2 rounded override-toggle pl-2"
                            labelPosition="left"
                            label=""
                            :checked="certificate.is_external"
                            labelClasses="uppercase font-semibold text-sm"
                            @value-changed="(value) => {
            certificate.is_external = value;
            if (value) certificate.is_internal = !value;
          }"
                    />
                  </div>

                  <!-- Expiry Date Section -->
                  <div class="w-7/10 p-1 flex flex-col">
                    <label class="block uppercase font-semibold text-sm">Expiry Date</label>
                    <Calendar appendTo="body" class="w-full"
                              dateFormat="dd/mm/yy"
                              v-model="certificate.expiry_date" :showIcon="true"
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div class="flex mt-4 pl-5">
            <div class="w-1/7">
              <label class="uppercase font-semibold text-sm">Job Sheet No.</label>
              <input
                  class="w-full bge-input bge-input-spacing rounded"
                  disabled
                  v-model="certificate.job_sheet_no"
                  :readonly="false"
                  type="text"
              />
            </div>
            <div class="w-1/7 ml-4">
              <label class="uppercase font-semibold text-sm">Job Type<span class="text-red-400">*</span></label>
              <select
                  v-model="certificate.job_type"
                  class="w-full bge-input bge-input-spacing rounded"
                  title="Drives the service or calibration dates on the equipment record"
              >
                <option value="">Select job type</option>
                <option v-for="type in jobTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>




      <component
          v-if="current_certificate_template"
          :is="current_certificate_template"
          :calibration_details="calibrationData"
          @update:calibration_details="calibrationData = $event"
          :certificate_id="certificate_id"
          ref="certificate_form"
          class="mt-4"
          :key="templateKey"
      />


      <portal to="overlay-outlet">
        <PDFViewer :showing="pdfViewerData.isShowingViewer" :pdfBlob="pdfViewerData.pdfData"
                   :documentName="pdfViewerData.documentName" @close="closeViewer"/>
      </portal>

    </main>



    <portal to="overlay-outlet">
      <panel
          :showing="findEquipmentPanelOpen"
          @close="findEquipmentPanelOpen = false"
          title="Find Equipment"
      >
        <FindEquipment
            @complete="applyEquipmentDetails"
        />
      </panel>
    </portal>

  </form>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Panel from "@/components/Panel.vue";
import Spinner from "@/components/Spinner.vue";
import ContactAutocomplete from '@/components/AutoComplete/ContactAutoComplete.vue';
import EquipmentWithSerialNumberAutoComplete from '@/components/AutoComplete/EquipmentWithSerialNumberAutoComplete.vue';
import AppButton from '@/components/Button.vue';
import Icon from '@/components/Icon.vue';
import notificationMixin from "@/mixins/notificationMixin";
import StaffAutocomplete from "@/components/AutoComplete/StaffAutoComplete.vue";
import printJS from "print-js";

const Toggle = () => import("@/components/Toggle.vue");
const PDFViewer = () => import("@/components/PDFViewer.vue");

import ExhaustGasAnalyser from './Components/ExhaustGasAnalyser.vue'
import SmokeMeter from './Components/SmokeMeter.vue'
import Decelerometer from './Components/Decelerometer.vue'
import RollerBrakeTester from './Components/RollerBrakeTester.vue'
import RollerBrakeTesterMC from './Components/RollerBrakeTesterMC.vue'
import Statement from './Components/Statement.vue'
import LoadProof from './Components/LoadProofCertificatet.vue'
import ExaminationVL from './Components/ExaminationVL.vue'
import ServiceReport from './Components/ServiceReport.vue'
import CoshhLev from './Components/CoshhLev.vue'
import FindEquipment from "@/views/HR/Engineer/Certificate/Components/FindEquipment.vue";


const CERTIFICATE_CONFIG = {
  'calibration - ags':      { component: ExhaustGasAnalyser, dataField: 'details', resetData: () => ({ details: null }), reportValid: true },
  'calibration - opa':      { component: SmokeMeter,         dataField: 'details', resetData: () => ({ details: null }), reportValid: true },
  'calibration - dc7500':   { component: Decelerometer,      dataField: 'details', resetData: () => ({ details: null }), reportValid: true },
  'calibration - rbt':      { component: RollerBrakeTester,  dataField: 'details', resetData: () => ({ details: null }), reportValid: true },
  'calibration - rbt-mc':   { component: RollerBrakeTesterMC,dataField: 'details', resetData: () => ({ details: null }), reportValid: true },

  // statement-only templates expect details = { statement: string }
  'calibration - hlt':      { component: Statement,          dataField: 'details', resetData: () => ({ details: null }), reportValid: true },
  'calibration - pd':       { component: Statement,          dataField: 'details', resetData: () => ({ details: null }), reportValid: false },
  'examination - all':      { component: Statement,          dataField: 'details', resetData: () => ({ details: null }), reportValid: true },
  'service':                { component: Statement,          dataField: 'details', resetData: () => ({ details: null }), reportValid: true },

  // load proof variants expect details = { load_proof: {...}, statement?: string }
  'load proof - vl':        { component: LoadProof,          dataField: 'details', resetData: () => ({ details: null }), reportValid: true },
  'examination - vl':       { component: ExaminationVL,      dataField: 'details', resetData: () => ({ details: null }), reportValid: true },

  // service report expects details = ServiceReport shape
  'service - report':       { component: ServiceReport,      dataField: 'details', resetData: () => ({ details: null }), reportValid: true },
  'service - report - vl':  { component: ServiceReport,      dataField: 'details', resetData: () => ({ details: null }), reportValid: true },

  // COSHH - LEV expects details = CoshhLev shape (test1/test2 points, visual_checks, fan_details, ...)
  'coshh - lev':             { component: CoshhLev,          dataField: 'details', resetData: () => ({ details: null }), reportValid: true },
};



export default {
  name: "ManageCertificate",
  mixins: [notificationMixin],
  components: {
    FindEquipment,
    StaffAutocomplete,
    PageHeader,
    Panel,
    Spinner,
    ContactAutocomplete,
    EquipmentWithSerialNumberAutoComplete,
    AppButton,
    Icon,
    Toggle,
    PDFViewer,
    ExhaustGasAnalyser,
    SmokeMeter,
    Decelerometer,
    RollerBrakeTester,
    RollerBrakeTesterMC,
    Statement,
    LoadProof,
    ServiceReport,
    ExaminationVL,
    CoshhLev

  },
  props: {
    certificate_id: String,
  },
  data() {
    return {
      isBusy: false,
      current_certificate_template: null,
      current_certificate_type: null,
      report_type: "",
      findEquipmentPanelOpen: false,
      certificate: {
        status: "New",
        job_sheet_id: "",
        job_sheet_no: "",
        job_type: "",
        equipment: {
          equipment_id: '',
          serial_number: '',
        },
        is_internal: false,
        is_external: false,
        is_connected: false,
        load_proof: {
          design_load_capacity: ''
        },
        exhaust_gas_analyser:
            {
              initial_calibration: {
                calibration_gas: {},
                gas_pef: {},
                analyser_reading: {},
              },
              final_calibration: {
                calibration_gas: {},
                gas_pef: {},
                analyser_reading: {},
              }
            },

        smoke_meter: {
          initial_calibration: {
            ambient_temperature: null,
            garage_filter: {
              true_filter_value: null,
              smoke_meter_reading: null
            },
            reference_filter_1: {
              true_filter_value: null,
              smoke_meter_reading: null
            },
            reference_filter_2: {
              true_filter_value: null,
              smoke_meter_reading: null
            },
            reference_filter_3: {
              true_filter_value: null,
              smoke_meter_reading: null
            }
          },
          final_calibration: {
            ambient_temperature: null,
            garage_filter: {
              true_filter_value: null,
              smoke_meter_reading: null
            },
            reference_filter_1: {
              true_filter_value: null,
              smoke_meter_reading: null
            },
            reference_filter_2: {
              true_filter_value: null,
              smoke_meter_reading: null
            },
            reference_filter_3: {
              true_filter_value: null,
              smoke_meter_reading: null
            }
          }
        },
      },
      pdfViewerData: {
        documentName: "",
        pdfData: null,
        isShowingViewer: false,
      },
      pdfUrl: null,
      validationErrors: {},

    };
  },
  computed: {
    pageTitle() {
      return this.certificate_id ? "Update Certificate" : "Create Certificate";
    },

    jobTypes() {
      return ['SERVICE', 'CALIBRATION', 'REPAIR', 'INSTALL', 'WARRANTY', 'OTHER'];
    },

    getCertificateNo() {
      return (this.certificate && this.certificate.certificate_number) ? this.certificate.certificate_number : "";
    },

    // Status and last-saved time sit in the header so staff can see what
    // state a certificate is in before they touch it.
    certificateStatusLabel() {
      if (!this.certificate_id || !this.certificate) return "";
      return this.certificate.status || "New";
    },

    certificateStatusClass() {
      return this.certificateStatusLabel === "Completed"
          ? "bg-green-200 text-green-800"
          : "bg-gray-200 text-gray-700";
    },

    certificateLastUpdated() {
      if (!this.certificate_id || !this.certificate) return "";
      const stamp = this.certificate.updated_at || this.certificate.created_at;
      return stamp ? this.$moment.unix(stamp).format("DD/MM/YYYY HH:mm") : "";
    },

    currentConfig() {
      var n = ((this.certificate && this.certificate.type && this.certificate.type.name) || '').trim().toLowerCase();
      return CERTIFICATE_CONFIG[n] || null;
    },

    calibrationData: {
      get() {
        if (!this.certificate.details) this.$set(this.certificate, 'details', {});
        return this.certificate.details;
      },
      set(v) {
        this.$set(this.certificate, 'details', v || {});
      }
    },

    templateKey() {
      return ((this.certificate && this.certificate.type && this.certificate.type.name) || '').trim().toLowerCase();
    }
  },

  async mounted() {

    this.isBusy = true;
    if (this.certificate_id) {
      const certificate = await this.loadCertificateById();
      this.certificate = this.formatCertificateData(certificate);
      this.loadCertificate();
      await this.prefillJobTypeFromJobSheet();
      this.isBusy = false;
      return;
    } else {
      const {
        job_sheet_id,
        certificate_type_id,
        certificate_type_name,
        job_type
      } = this.$route.query;

      if (job_sheet_id) {
        await this.loadJobSheetData(job_sheet_id,certificate_type_id);
      }

      if (!this.certificate_id) {
        const existed = await this.tryFindExistingCertificate(job_sheet_id, certificate_type_id);
        if (existed) return;
      }


      if (certificate_type_id && certificate_type_name) {
        this.selectedCertificateType = {
          data_master_id: certificate_type_id,
          name: certificate_type_name
        };

        this.certificate.type = {
          data_master_id: certificate_type_id,
          name: certificate_type_name
        };

        await this.handleTypeChange();
      }

      if (job_type) {
        const jobTypeMap = {
          S: 'SERVICE',
          C: 'CALIBRATION',
          R: 'REPAIR',
          O: 'OTHER',
          I: 'INSTALL',
          W: 'WARRANTY'
        };

        const decodedJobType = jobTypeMap[job_type.toUpperCase()] || null;
        if (decodedJobType) {
          this.certificate.job_type = decodedJobType;
        }
      }
    }
    this.isBusy = false;
  },

  watch: {
    certificate_id: {
      immediate: false,
      async handler (newId, oldId) {
        if (newId && newId !== oldId) {
          await this.reloadCertificate(newId);
        }
      }
    }
  },
  methods: {
    async reloadCertificate(id) {
      this.isBusy = true;
      this.resetLocalState();
      const certificate = await this.loadCertificateById(id); // accept id
      this.certificate = this.formatCertificateData(certificate);
      this.loadCertificate();
      await this.prefillJobTypeFromJobSheet();
      this.isBusy = false;
    },
    resetLocalState() {
      this.current_certificate_template = null;
      this.current_certificate_type = null;
      this.validationErrors = {};
    },
    async tryFindExistingCertificate(jobSheetId, certificateTypeId) {
      try {
        const cert = this.certificate || {};

        if (!jobSheetId   || !certificateTypeId) return null;

        const existing = await this.CertificateService.getCertificateByJobAndType({
          job_sheet_id: jobSheetId,
          certificate_type_id: certificateTypeId,
        });

        if (existing && existing.certificate_id) {
          const equipmentModel = cert.equipment_model || 'this equipment';
          const jobNo = cert.job_sheet_no || '';

          const proceed = await this.confirmAction({
            header: 'Existing certificate found',
            message: 'We found a certificate for "' + equipmentModel + '" on job "' + jobNo + '". Open it now?',
            acceptLabel: 'Open existing',
            rejectLabel: 'Stay here',
          });

          if (proceed) {
            await this.$router.replace({
              path: '/internal/service/certificate/edit/' + existing.certificate_id,
            });
            return existing;
          }
        }
      } catch (err) {
        console.error('Lookup existing certificate failed:', err);
      }
      return null;
    },
    // Pre-select the job type from the job sheet when the certificate has none.
    applyJobTypeFromJobSheet(job) {
      if (!job || this.certificate.job_type) return;
      const jobType = String(job.job_type || '').toUpperCase();
      if (this.jobTypes.includes(jobType)) {
        this.certificate.job_type = jobType;
      }
    },

    // Older certificates were saved without a job type. On open, fill it from
    // the linked job sheet so the user only has to confirm and save.
    async prefillJobTypeFromJobSheet() {
      if (this.certificate.job_type || !this.certificate.job_sheet_id) return;
      try {
        const job = await this.JobSheetService.getJobSheetById(this.certificate.job_sheet_id);
        this.applyJobTypeFromJobSheet(job);
      } catch (error) {
        console.error('Could not read the job sheet to pre-select the job type:', error);
      }
    },

    async loadJobSheetData(jobSheetId,certificateTypeId) {
      try {
        const job = await this.JobSheetService.getJobSheetById(jobSheetId);

        this.certificate.job_sheet_id = job.job_sheet_id;
        this.certificate.job_sheet_no = job.job_sheet_no;

        this.certificate.customer = {
          contact_id: (job.customer && job.customer.contact_id) ? job.customer.contact_id : '',
          name: (job.customer && job.customer.name) ? job.customer.name : ''
        };

        const engineer = job.engineer || {};
        this.certificate.engineer = {
          staff_id: engineer.staff_id || '',
          name: ((engineer.first_name || '') + ' ' + (engineer.last_name || '')).trim()
        };

        const equipment = job.job_equipment || {};
        this.certificate.equipment_make = equipment.make || '';
        this.certificate.equipment_model = equipment.product_code || '';
        this.certificate.equipment_description = equipment.name || '';
        await this.applyEquipmentModelFromRecord(job.equipment_id);

        const jobScope = job.type === 'internal' ? 'internal' : 'external'; // default to external if unknown
        this.certificate.scope = jobScope;
        this.certificate.is_internal = jobScope === 'internal';
        this.certificate.is_external = jobScope === 'external';

        this.applyJobTypeFromJobSheet(job);

        this.certificate.status = 'New';

        this.certificate.equipment = {
          serial_number: job.job_equipment && job.job_equipment.serial_number ? job.job_equipment.serial_number : null,
          equipment_id: job.equipment && job.equipment.equipment_id ? job.equipment.equipment_id : null
        };

        if (
            job.certificates &&
            Array.isArray(job.certificates)
        ) {
          const matchedCert = job.certificates.find(
              c => c.certificate_type_id === certificateTypeId
          );
          if (
              matchedCert &&
              Array.isArray(matchedCert.service_checks) &&
              matchedCert.service_checks.length > 0
          ) {
            if (!this.certificate.service_report) {
              this.$set(this.certificate, 'service_report', {});
            }
            this.$set(this.certificate.service_report, 'service_questions', matchedCert.service_checks);
          }
          if (matchedCert && matchedCert.statement && matchedCert.statement.length > 0) {
            this.$set(this.certificate, 'statement', matchedCert.statement);
          }
        }



      } catch (error) {
        this.notifyError('Failed to load job sheet for certificate.');
        console.error(error);
      }
    },


    created() {
    },

    async clickFindEquipmentPanel() {
      this.findEquipmentPanelOpen = true;
    },
    // The certificate's Equipment Model is the Model on the equipment record,
    // falling back to the product code when the equipment has none.
    equipmentModelFor(equipment) {
      if (!equipment) return '';
      const model = typeof equipment.model_name === 'string' ? equipment.model_name.trim() : '';
      return model || equipment.product_code || '';
    },

    async applyEquipmentModelFromRecord(equipmentId) {
      if (!equipmentId) return;
      try {
        const equipment = await this.EquipmentService.getEquipmentItemById(equipmentId);
        const model = this.equipmentModelFor(equipment);
        if (model) this.certificate.equipment_model = model;
      } catch (error) {
        console.error('Could not read the equipment record for its model:', error);
      }
    },

    async applyEquipmentDetails(data) {
      if (!data) {
        return;
      }
      this.certificate.equipment_model = this.equipmentModelFor(data);
      this.certificate.equipment = {
        equipment_id: data.equipment_id || '',
        serial_number: data.serial_number || '',
      };
      this.certificate.equipment_description = data.name || '';

      if (data.owner) {
        this.certificate.customer = {};
        this.certificate.customer.name = data.owner.name || '';
        this.certificate.customer.contact_id = data.owner.contact_id || '';
      }
      if (data.certificate_type) {
        this.certificate.type = {
          data_master_id: data.certificate_type.data_master_id,
          name: data.certificate_type.name
        }
      }

      if (
          data.certificate_type &&
          Array.isArray(data.certificate_type.service_checks) &&
          data.certificate_type.service_checks.length > 0
      ) {
        if (!this.certificate.details) {
          this.$set(this.certificate, 'details', {});
        }

        const existing =
            (this.certificate.details && this.certificate.details.service_questions) || [];

        const merged = data.certificate_type.service_checks.map((q) => {
          const prev = existing.find(
              (p) => p.question_text === q.question_text
          ) || {};
          return {
            question_text: q.question_text || '',
            outcome: prev.outcome || ''
          };
        });

        this.$set(this.certificate.details, 'service_questions', merged);
      }


      await this.handleTypeChange();

      if (data.certificate_type && data.certificate_type.description) {
        this.$set(this.certificate, 'statement', data.certificate_type.description);
      }


      this.updateCertificateDates(data);


      this.findEquipmentPanelOpen = false;
    },
    async handleEquipmentSelected(selectedEquipment) {
      this.isBusy = true;
      this.certificate.equipment_model = selectedEquipment.value.product_code;
      this.certificate.equipment_description = selectedEquipment.value.name;
      if (!selectedEquipment || !selectedEquipment.value) {
        this.isBusy = false;
        return;
      }

      const equipmentDetails = await this.EquipmentService.getEquipmentItemById(selectedEquipment.value.equipment_id)

      if (!equipmentDetails || !equipmentDetails.owner) {
        this.isBusy = false;
        return;
      }

      const customer = {
        contact_id: equipmentDetails.owner.contact_id,
        name: equipmentDetails.owner.name
      };
      this.certificate.customer = customer;
      this.certificate.equipment_model = this.equipmentModelFor(equipmentDetails) || this.certificate.equipment_model;
      this.updateCertificateDates(equipmentDetails);

      this.isBusy = false;
    },

    updateCertificateDates(equipmentDetails) {
      const todayMoment = this.$moment();
      const todayDate = todayMoment.toDate();
      this.certificate.issue_date = todayDate;

      if (!equipmentDetails || equipmentDetails.calibration_interval == null) {
        this.certificate.expiry_date = todayDate;
      } else {
        const expiryMoment = todayMoment.clone().add(equipmentDetails.calibration_interval, 'months');
        const expiryDate = expiryMoment.toDate();
        this.certificate.expiry_date = expiryDate;
      }
    },
    // Save & Complete: saves the form and marks the certificate Completed in
    // one go. A brand new certificate is created first, then completed.
    async markCertificateComplete() {
      if (!this.certificate || !this.certificate.type || !this.certificate.type.name) {
        this.notifyWarning("Use Find & Select Equipment to choose the equipment and its certificate type before completing.");
        return;
      }

      // Validation: Ensure dates are present
      if (!this.certificate.issue_date || !this.certificate.expiry_date) {
        this.notifyWarning("Issue Date and Expiry Date are mandatory.");
        return;
      }

      // Validation: the job type decides which equipment dates get updated
      if (!this.certificate.job_type) {
        this.notifyWarning("Select the Job Type before marking this certificate complete.");
        return;
      }

      // Validation: Issue Date must be before Expiry Date
      const issueDate = this.$moment(this.certificate.issue_date);
      const expiryDate = this.$moment(this.certificate.expiry_date);

      if (!issueDate.isBefore(expiryDate)) {
        this.notifyWarning("Issue Date must be before Expiry Date.");
        return;
      }

      if (this.$refs.certificate_form && typeof this.$refs.certificate_form.isValid === 'function' && !this.$refs.certificate_form.isValid()) {
        // Forms that can point at the exact boxes (COSHH-LEV) highlight them and name the first problem
        const form = this.$refs.certificate_form;
        const problems = typeof form.showValidation === 'function' ? form.showValidation() : [];
        if (problems.length) {
          const more = problems.length > 1 ? ` (and ${problems.length - 1} more, marked in red)` : '';
          this.notifyWarning(`${problems[0]}${more}`);
        } else {
          this.notifyWarning("Please complete all mandatory fields before marking this certificate complete.");
        }
        return;
      }

      this.isBusy = true;

      let createdId = null;
      try {
        const formattedCertificate = this.unformatCertificateData(this.certificate);

        let certificateId = this.certificate_id;
        if (!certificateId) {
          const created = await this.CertificateService.createCertificate(formattedCertificate);
          certificateId = created && created.certificate_id;
          if (!certificateId) {
            throw new Error("The certificate was not created.");
          }
          createdId = certificateId;
        }

        formattedCertificate.status = 'Completed';
        await this.CertificateService.updateCertificate(certificateId, formattedCertificate);

        this.certificate.status = 'Completed'; // update UI state
        this.notifySuccess("Certificate saved and marked as Complete.");

        if (createdId) {
          await this.$router.push({ path: `/internal/service/certificate/edit/${createdId}` });
        } else {
          await this.refreshSavedState();
        }
      } catch (error) {
        console.error(error);
        if (createdId) {
          // Saved as New but not completed: open it so nothing is lost.
          this.notifyError("Certificate saved, but it could not be marked as Complete. Press Save & Complete again.");
          await this.$router.push({ path: `/internal/service/certificate/edit/${createdId}` });
        } else {
          this.notifyError("Failed to save and complete the certificate.");
        }
      } finally {
        this.isBusy = false;
      }
    },

    handleEquipmentCleared() {
      this.certificate.equipment_model = null;
      this.certificate.equipment_description = null;
      this.certificate.customer = null;
    },
    handleEquipmentUnselected() {
      this.certificate.equipment_model = null;
      this.certificate.equipment_description = null;
      this.certificate.customer = null;
    },

    loadCertificate() {
      if (!this.certificate || !this.certificate.type || !this.certificate.type.name) {
        this.notifyWarning("Use Find & Select Equipment to choose the equipment and its certificate type. The certificate form loads from that.");
        return;
      }

      const certificate_type = this.certificate.type.name.trim().toLowerCase();
      this.current_certificate_type = this.certificate.type;
      const typeConfig = CERTIFICATE_CONFIG[certificate_type];

      if (typeConfig) {
        this.current_certificate_template = typeConfig.component;
      } else {
        console.warn(`No template available for the certificate type: ${certificate_type}`);
        this.current_certificate_template = null;
      }

      if (!this.current_certificate_template) {
        this.notifyWarning("Failed to load certificate template. Please check the certificate type.");
      }
    },

    async handleTypeChange() {

      const new_certificate_type_name = this.certificate && this.certificate.type && this.certificate.type.name
          ? this.certificate.type.name.trim().toLowerCase()
          : '';


      const current_certificate_type_name = this.current_certificate_type && this.current_certificate_type.name
          ? this.current_certificate_type.name.trim().toLowerCase()
          : '';


      if (current_certificate_type_name && current_certificate_type_name !== new_certificate_type_name && this.modelHasData(current_certificate_type_name)) {
        const confirmation = await this.confirmAction({
          message: 'Changing the model will reset the existing calibration data for other models. Do you want to continue?',
          header: 'Confirm Model Change',
          acceptLabel: 'Continue',
          rejectLabel: 'Cancel',
        });
        if (!confirmation) {
          this.certificate.type = this.current_certificate_type;
          return;
        }

        this.resetOtherModelData(new_certificate_type_name);
      }

      this.current_certificate_type = this.certificate.type;
      this.loadCertificate(new_certificate_type_name);
    },


    modelHasData(type) {
      const certificate_type = type.toLowerCase();
      const config = CERTIFICATE_CONFIG[certificate_type];
      if (config && this.certificate[config.dataField] !== null) {
        return true;
      }
      return false;
    },

    resetOtherModelData(excludeType) {
      Object.entries(CERTIFICATE_CONFIG).forEach(([type, config]) => {
        if (type !== excludeType) {
          Object.assign(this.certificate, config.resetData());
        }
      });
    },


    async loadCertificateById() {
      try {
        const certificateData = await this.CertificateService.getCertificateById(this.certificate_id);
        return certificateData;
      } catch (error) {
        console.error("Error loading schedule:", error);
      }
    },

    // After a save, pick up the status and last-saved time the server wrote
    // so the header is right without a page refresh. The form itself is left
    // as the user has it.
    async refreshSavedState() {
      const saved = await this.loadCertificateById();
      if (!saved || !this.certificate) return;
      this.$set(this.certificate, "updated_at", saved.updated_at);
      if (saved.status) this.$set(this.certificate, "status", saved.status);
    },

    formatCertificateData(certificate) {

      let formattedCertificate = JSON.parse(JSON.stringify(certificate));

      // Formatting job_start_date
      if (formattedCertificate.issue_date) {
        let issueMoment = this.$moment.unix(formattedCertificate.issue_date);
        formattedCertificate.issue_date = issueMoment.toDate();
      }

      // Formatting job_end_date
      if (formattedCertificate.expiry_date) {
        let expiryMoment = this.$moment.unix(formattedCertificate.expiry_date);
        formattedCertificate.expiry_date = expiryMoment.toDate();
      }

      if (formattedCertificate.scope != null) {
        switch (formattedCertificate.scope) {
          case 'external':
            formattedCertificate.is_external = true;
            formattedCertificate.is_internal = false;
            break;
          case 'internal':
            formattedCertificate.is_internal = true;
            formattedCertificate.is_external = false;
            break;
          default:
            break;
        }
      }

      if (!formattedCertificate.status) {
        formattedCertificate.status = "New";
      }

      return formattedCertificate;
    },

    unformatCertificateData(formattedCertificate) {
      let certificate = JSON.parse(JSON.stringify(formattedCertificate));


      // Reverting issue_date
      if (certificate.issue_date) {
        certificate.issue_date = this.$moment(certificate.issue_date).unix();
      }

      // Reverting expiry_date
      if (certificate.expiry_date) {
        certificate.expiry_date = this.$moment(certificate.expiry_date).unix();
      }

      // Reverting scope
      if (certificate.is_internal != null || certificate.is_external != null) {
        if (certificate.is_internal) {
          certificate.scope = 'internal';
        } else if (certificate.is_external) {
          certificate.scope = 'external';
        } else {
          certificate.scope = null;
        }
        delete certificate.is_internal;
        delete certificate.is_external;
      }


      const type = certificate.type.name.trim().toLowerCase();
      const config = CERTIFICATE_CONFIG[type];
      if (!config) {
        console.error('Configuration not found for certificate type:', type);
        return;
      }

      Object.keys(CERTIFICATE_CONFIG).forEach(certType => {
        const field = CERTIFICATE_CONFIG[certType].dataField;
        if (field !== config.dataField) {
          certificate[field] = null;
        }
      });

      certificate[config.dataField] = this.$refs.certificate_form.getCurrentData();

      return certificate;
    },


    async printCertificate() {

      if (!this.certificate_id) {
        this.notifyWarning("Missing certificate ID . Please ensure all required fields are filled.");
        return;
      }


      if (!this.certificate || !this.certificate.type || !this.certificate.type.name) {
        this.notifyWarning("Use Find & Select Equipment to choose the equipment and its certificate type before printing.");
        return;
      }

      const certificate_type = this.certificate.type.name.trim().toLowerCase();


      if (!this.isValidReportType(certificate_type)) {
        this.notifyWarning("Invalid report type. Please select a valid report type before printing.");
        return;
      }

      try {
        const response = await this.CertificateService.getReport(this.certificate_id, certificate_type);

        this.pdfViewerData = {
          pdfData: response,
          documentName: `${certificate_type.replace(/-/g, ' ')} Report`,
          isShowingViewer: true,
        };
      } catch (error) {
        console.error('Error loading PDF:', error);
        this.notifyWarning("Failed to load the PDF document. Please try again.");
      }
    },

    isValidReportType(reportType) {

      const normalizedType = reportType.trim().toLowerCase();

      return CERTIFICATE_CONFIG[normalizedType] && CERTIFICATE_CONFIG[normalizedType].reportValid;
    },

    closeViewer() {
      this.pdfViewerData = {
        pdfData: null,
        documentName: "Gas Analyzer Report",
        isShowingViewer: false,
      };
    },
    formatValidationMessage(message) {
      if (!message || typeof message !== 'string') return '';

      message = message.replace(/["']/g, '');

      message = message.replace(/[_\.]/g, ' ');

      message = message.replace(/\s+/g, ' ').trim();

      message = message.replace(/\b\w/g, char => char.toUpperCase());

      return message;
    },
    async saveCertificate() {
      if (!this.certificate || !this.certificate.type || !this.certificate.type.name) {
        this.notifyWarning("Use Find & Select Equipment to choose the equipment and its certificate type before saving.");
        return;
      }

      this.isBusy = true;

      const formattedCertificate = this.unformatCertificateData(this.certificate);

      try {
        let savedCertificate;

        if (this.certificate_id) {
          savedCertificate = await this.CertificateService.updateCertificate(this.certificate_id, formattedCertificate);
          this.notifySuccess("Certificate updated successfully");
          await this.refreshSavedState();
        } else {
          savedCertificate = await this.CertificateService.createCertificate(formattedCertificate);
          const idToRedirect = savedCertificate.certificate_id || this.certificate_id;
          await this.$router.push({
            path: `/internal/service/certificate/edit/${idToRedirect}`,
          });
          this.notifySuccess("Certificate created successfully");
        }

      } catch (error) {
        const errorMessage = this.certificate_id ? "Couldn't update certificate" : "Couldn't create certificate";

        const backendError = (error && error.response && error.response.data)
            ? error.response.data
            : (error && error.data)
                ? error.data
                : null;

        if (backendError && Array.isArray(backendError.message)) {
          this.validationErrors = backendError.message;

          for (let i = 0; i < this.validationErrors.length; i++) {
            const formattedMsg = this.formatValidationMessage(this.validationErrors[0]);
            this.notifyError(formattedMsg);
            break;
          }

        } else {
          this.validationErrors = [];
          const fallbackMsg = backendError && backendError.message
              ? backendError.message
              : errorMessage;
          this.notifyError(fallbackMsg);
        }

        console.error(backendError && backendError.message ? backendError.message : error);

      } finally {
        this.isBusy = false;
      }
    },

    submitCertificate: async function (event) {
      event.preventDefault();
    },

  }
}
;
</script>

<style scoped>
::v-deep .override-toggle .w-full {
  width: auto !important;
}

::v-deep .override-toggle {
  width: auto !important;
}
</style>
